import { Button, InputGroup, color } from "@funded-here-interface/common";
import { FC, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { RootState } from "@funded-here-interface/investor-onboarding/src/store";
import { useStyle } from "./RepresentativeDetails.styles";
import { Checkbox, MantineProvider } from "@mantine/core";
import AuthRepInfo from "../../components/AuthRepInfo/AuthRepInfo";
import OnboardingLayout from "../../layouts/OnboardingLayout/OnboardingLayout";
import useGetOrgDraft from "../../hooks/useGetOrgDraft";
import useSaveOrgDraft from "../../hooks/useSaveOrgDraft";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Line from "../../assets/LineHorizontal.png";
import ShareholdersDirectors from "../../components/ShareholdersDirectors/ShareholdersDirectors";
import Auth from "../../components/Auth/Auth";
import { ShareholderDirector } from "../../features/merchant/rep";
import {
  CompanyRole,
  ShareholderDirectorField,
} from "@funded-here-interface/common/src/constant/enum";

// Custom hooks and utilities
import { useRepresentativeFormState } from "./hooks/useRepresentativeFormState";
import { useAuthRepresentativeSync } from "./hooks/useAuthRepresentativeSync";
import { useDirectorSync } from "./hooks/useDirectorSync";
import { validateRepresentativeForm } from "./utils/validation";
import {
  getDirectorEmails,
  findDirectorByEmail,
} from "./utils/directorHelpers";

const RepresentativeDetails: FC = () => {
  const { classes } = useStyle();
  const Navigate = useNavigate();
  const { userFirstName, userLastName, userEmail, token } = useSelector(
    (state: RootState) => state.auth
  );
  const userId = useSelector((state: RootState) => state.auth.userId);

  // API hooks
  const getOrgDraftMutation = useGetOrgDraft();
  const saveOrgDraftMutation = useSaveOrgDraft();

  // Checkbox state
  const [checked, setChecked] = useState(false);
  const [directorChecked, setDirectorChecked] = useState(false);

  // Shareholders/Directors state
  const [numOfShareholdersDirectors, setNumOfShareholdersDirectors] =
    useState(1);
  const [shareholdersDirectors, setShareholdersDirectors] = useState<
    ShareholderDirector[]
  >([]);

  // Form state management hook
  const {
    formState,
    errors,
    setAllErrors,
    clearError,
    clearRepresentativeErrors,
    updateField,
    updateFields,
    createNameChangeHandler,
  } = useRepresentativeFormState({
    repFirstName: "",
    repLastName: "",
    repEmail: "",
    representative_title: "",
    adminRole: "",
  });

  // Sync authenticated user data to representative fields
  useAuthRepresentativeSync({
    checked,
    userFirstName,
    userLastName,
    userEmail,
    adminRole: formState.adminRole,
    updateFields,
    clearRepresentativeErrors,
  });

  // Sync director data to representative fields
  useDirectorSync({
    checked,
    directorChecked,
    setDirectorChecked,
    repEmail: formState.repEmail,
    shareholdersDirectors,
    updateFields,
    clearRepresentativeErrors,
  });

  // Compute director emails for UI
  const directorEmails = getDirectorEmails(shareholdersDirectors);
  const allDirectorsEmailPresent = directorEmails.length > 0;

  /**
   * Handle back navigation - save draft and navigate
   */
  const handleBack = () => {
    saveDraft(false);
    Navigate("/biz-profile");
  };

  /**
   * Handle next navigation - validate and proceed
   */
  const handleNext = () => {
    saveDraft(false);

    const validation = validateRepresentativeForm({
      repFirstName: formState.repFirstName,
      repLastName: formState.repLastName,
      repEmail: formState.repEmail,
      representative_title: formState.representative_title,
      adminRole: formState.adminRole,
      shareholdersDirectors,
      numOfShareholdersDirectors,
    });

    if (validation.isValid) {
      Navigate("/bank-details");
    } else {
      setAllErrors(validation.errors);
    }
  };

  /**
   * Update shareholder/director field value
   * Also syncs to representative fields if currently selected director
   */
  const updateShareholderDirectorValue = (
    index: number,
    field: ShareholderDirectorField,
    value: string
  ) => {
    // Map field enum to error key suffix
    const fieldToErrorKey: Record<ShareholderDirectorField, string> = {
      [ShareholderDirectorField.FIRSTNAME]: "firstName",
      [ShareholderDirectorField.LASTNAME]: "lastName",
      [ShareholderDirectorField.EMAIL]: "email",
      [ShareholderDirectorField.ROLE]: "role",
    };

    // Clear error for this director field
    clearError(`shareholdersDirectors.${index}.${fieldToErrorKey[field]}`);

    // Sync to representative fields if this director is currently selected
    if (
      shareholdersDirectors[index]?.email === formState.repEmail &&
      directorChecked
    ) {
      if (field === ShareholderDirectorField.EMAIL) {
        updateField("repEmail", value);
      }
      if (field === ShareholderDirectorField.FIRSTNAME) {
        updateField("repFirstName", value);
      }
      if (field === ShareholderDirectorField.LASTNAME) {
        updateField("repLastName", value);
      }
    }

    // Update shareholders/directors array
    setShareholdersDirectors((prev) => {
      const updatedShareholdersDirectors: ShareholderDirector[] = [...prev];
      updatedShareholdersDirectors[index] = {
        ...updatedShareholdersDirectors[index],
        [field]: value,
        [ShareholderDirectorField.ROLE]: CompanyRole.DIRECTOR,
      };
      return updatedShareholdersDirectors;
    });
  };

  /**
   * Handle change in number of shareholders/directors
   */
  const onNumberOfShareholdersDirectorsChange = (value: number) => {
    setNumOfShareholdersDirectors(Number(value));

    if (value < numOfShareholdersDirectors && shareholdersDirectors[value]) {
      const updatedShareholdersDirectors = [...shareholdersDirectors];
      updatedShareholdersDirectors.pop();
      setShareholdersDirectors(updatedShareholdersDirectors);
    }
  };

  /**
   * Remove a specific shareholder/director by index
   */
  const removeShareholderDirector = (index: number) => {
    const directorToRemove = shareholdersDirectors[index];

    // If the removed director was selected as the representative, clear rep fields
    if (directorChecked && directorToRemove?.email === formState.repEmail) {
      updateFields({
        repFirstName: "",
        repLastName: "",
        representative_title: "",
        repEmail: "",
      });
      setDirectorChecked(false);
    }

    const updatedShareholdersDirectors = [...shareholdersDirectors];
    updatedShareholdersDirectors.splice(index, 1);
    setShareholdersDirectors(updatedShareholdersDirectors);
    setNumOfShareholdersDirectors(numOfShareholdersDirectors - 1);
  };

  /**
   * Handle director email selection for authorized representative
   */
  const handleAuthDirectorEmailChange = (email: string) => {
    const directorInfo = findDirectorByEmail(shareholdersDirectors, email);
    if (directorInfo) {
      updateFields({
        repLastName: directorInfo.lastName,
        repFirstName: directorInfo.firstName,
        representative_title: "Director",
        repEmail: email,
      });
    }
  };

  /**
   * Fetch and initialize draft data on component mount
   */
  useEffect(() => {
    const initializeDraft = async () => {
      if (!userId) return;

      try {
        const data = await getOrgDraftMutation.mutateAsync({
          userId: parseInt(userId),
          token,
        });

        if (data) {
          let authChecked = data.authChecked || false;
          let directorChecked = data.directorChecked || false;
          let repFirstName = data.repFirstName || "";
          let repLastName = data.repLastName || "";
          let repEmail = data.repEmail || "";
          let repTitle = data.repTitle || "";
          let adminRole = data.userTitle || "";
          let shareholdersDirectors = data.shareHoldersDirectors || [];
          let numOfShareholdersDirectors = shareholdersDirectors.length || 1;

          const draftDirectorEmails = getDirectorEmails(shareholdersDirectors);
          const allDirectorsEmailPresent = draftDirectorEmails.length > 0;

          // If auth user is acting as representative, use their info
          if (authChecked) {
            repLastName = userLastName as string;
            repFirstName = userFirstName as string;
          }
          // If director is selected, validate and populate from director info
          else if (directorChecked && allDirectorsEmailPresent) {
            const directorInfo = findDirectorByEmail(
              shareholdersDirectors,
              repEmail
            );
            if (directorInfo) {
              repLastName = directorInfo.lastName;
              repFirstName = directorInfo.firstName;
              repTitle = "Director";
              repEmail = directorInfo.email;
            } else {
              // Director no longer valid, clear selection
              repLastName = "";
              repFirstName = "";
              repTitle = "";
              repEmail = "";
              directorChecked = false;
            }
          }

          // Update all state
          setChecked(authChecked);
          setDirectorChecked(directorChecked);
          updateFields({
            repFirstName,
            repLastName,
            repEmail,
            representative_title: repTitle,
            adminRole,
          });
          setShareholdersDirectors(shareholdersDirectors);
          setNumOfShareholdersDirectors(numOfShareholdersDirectors);
        }
      } catch (error) {
        toast.error(`Error loading draft: ${error}`);
      }
    };

    initializeDraft();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Save draft to backend
   */
  const saveDraft = (showToast: boolean = true) => {
    saveOrgDraftMutation.mutateAsync(
      {
        userId: parseInt(userId),
        token,
        repFirstName: formState.repFirstName,
        repLastName: formState.repLastName,
        repEmail: formState.repEmail,
        repTitle: formState.representative_title,
        userTitle: formState.adminRole,
        authChecked: checked,
        directorChecked: directorChecked,
        shareHoldersDirectors: shareholdersDirectors,
        numOfShareHoldersDirectors: numOfShareholdersDirectors,
      },
      {
        onSuccess: () => {
          if (showToast) toast.success("Draft Saved");
        },
        onError: (e) => {
          toast.error((e as Error).message);
        },
      }
    );
  };

  return (
    <OnboardingLayout
      step={2}
      showSteps={true}
      content={
        <div className={classes.container}>
          <AuthRepInfo />
          <div className={classes.inputBox}>
            <MantineProvider theme={{ cursorType: "pointer" }}>
              <Checkbox
                style={{ marginBottom: "20px" }}
                checked={checked}
                labelPosition="left"
                label={
                  <span style={{ fontWeight: "bold" }}>
                    Will you be acting as an Authorized Representative?
                  </span>
                }
                onChange={(event) => setChecked(event.currentTarget.checked)}
              />
            </MantineProvider>

            <InputGroup
              isErrored={errors.adminRole}
              onChange={createNameChangeHandler("adminRole")}
              labelText="Your role in the company"
              id="admin_role"
              value={formState.adminRole}
              errorMessage={!!errors.adminRole}
            />

            <img src={Line} alt="line" style={{ width: "190%" }} />

            <ShareholdersDirectors
              numOfShareholdersDirectors={numOfShareholdersDirectors}
              onNumberOfShareholdersDirectorsChange={
                onNumberOfShareholdersDirectorsChange
              }
              errors={errors}
              shareholdersDirectors={shareholdersDirectors}
              updateShareholderDirectorValue={updateShareholderDirectorValue}
              onRemoveDirector={removeShareholderDirector}
            />

            <img src={Line} alt="line" style={{ width: "190%" }} />

            {!checked && (
              <Auth
                numOfDirectors={numOfShareholdersDirectors}
                allDirectorsEmailPresent={allDirectorsEmailPresent}
                directorChecked={directorChecked}
                setDirectorChecked={setDirectorChecked}
                error={errors}
                repEmail={formState.repEmail}
                directorEmails={directorEmails}
                handleAuthDirectorEmailChange={handleAuthDirectorEmailChange}
                setRepEmail={(value: string) => updateField("repEmail", value)}
                setRepresentativeFirstName={(value: string) =>
                  updateField("repFirstName", value)
                }
                representativeFirstName={formState.repFirstName}
                setRepresentativeLastName={(value: string) =>
                  updateField("repLastName", value)
                }
                representativeLastName={formState.repLastName}
                setRepresentativeTitle={(value: string) =>
                  updateField("representative_title", value)
                }
                representativeTitle={formState.representative_title}
              />
            )}
            <div className={classes.buttonDiv}>
              <div className={classes.buttonWrapper}>
                <Button
                  onClick={handleBack}
                  backgroundColor={color.WHITE}
                  textColor={color.FHGREEN}
                  border="1px solid #338F86"
                  width="100%"
                >
                  Back
                </Button>
              </div>
              <div className={classes.buttonWrapper}>
                <Button
                  onClick={handleNext}
                  textColor={color.WHITE}
                  backgroundColor={color.FHGREEN}
                  width="100%"
                >
                  Next
                </Button>
              </div>
            </div>
            <div className={classes.buttonWrapper}>
              <Button
                onClick={() => saveDraft()}
                textColor={color.WHITE}
                backgroundColor={color.LOANTEXT}
                width="100%"
              >
                Save Draft
              </Button>
            </div>
          </div>
        </div>
      }
    />
  );
};

export default RepresentativeDetails;
